import { isNil } from "lodash";
import React, { FC, useEffect, useMemo, useState } from "react";

import {
  useSendMessageDirect,
  useSendNotificationsToAllChatMembers,
} from "~/hooks";
import {
  useChatConversationById,
  useChatConversationMarkUnread,
  useChatUpdateMessage,
} from "~/queries";
import {
  useChatAuthAccessToken,
  useChatAuthUserData,
  useChatMessageToEdit,
} from "~/ui/context";
import { useCurrentUser } from "~/utils";

import { Button } from "../../buttons";
import { Icon, LoadingIcon } from "../../icons";
import { ConversationDetails } from "./components/ConversationDetails";
import { ConversationMessageInput } from "./components/ConversationMessageInput";
import { Messages } from "./components/Messages";

export const ChatConversation: FC<{
  chatId: number;
}> = ({ chatId }) => {
  const accessToken = useChatAuthAccessToken();
  const { messageToEdit, setMessageToEdit } = useChatMessageToEdit();
  const [message, setMessage] = useState("");
  const { data: conversationDetails } = useChatConversationById(
    accessToken,
    chatId
  );
  const { chatAuthUser } = useChatAuthUserData();
  const currentUser = useCurrentUser();
  const {
    mutateAsync: updateMessageAsync,
    isLoading: isMessageUpdating,
  } = useChatUpdateMessage();
  const { sendMessageHandler, isMessageSending } = useSendMessageDirect();

  const { sendNotificationToAllMembers } = useSendNotificationsToAllChatMembers(
    chatId
  );
  const [hasClickedLoadMore, setHasClickedLoadMore] = useState(false);

  const { mutateAsync: unreadConversation } = useChatConversationMarkUnread();

  const onSuccess = () => {
    setMessage("");
    sendNotificationToAllMembers(message);
    unreadConversation([chatId, accessToken]);
    setHasClickedLoadMore(false);
  };
  const sendMessage = () => {
    if (isMessageUpdating || isMessageSending) {
      return;
    }
    if (message === "") return;
    if (isNil(messageToEdit)) {
      sendMessageHandler(chatId, message, onSuccess);
    } else {
      if (messageToEdit?.text === message) {
        setMessageToEdit(null);
        return;
      }
      updateMessageAsync([messageToEdit.id, accessToken, { message }]).then(
        () => {
          setMessage("");
          setMessageToEdit(null);
          setHasClickedLoadMore(false);
        }
      );
    }
  };

  useEffect(() => {
    if (messageToEdit) {
      setMessage(messageToEdit.text);
    } else {
      setMessage("");
    }
  }, [messageToEdit]);

  const isGlobal = useMemo(
    () => conversationDetails?.tags?.includes("global") ?? false,
    [conversationDetails?.tags]
  );
  const isAnnouncement = useMemo(() => chatId === 1, [chatId]);

  return (
    <div className="h-95 w-100 flex flex-col scroll-container">
      <ConversationDetails
        chatId={chatId}
        isGlobal={isGlobal}
        isConversationAdmin={currentUser?.user_id === 3}
        isOwner={conversationDetails?.created_by_id === chatAuthUser?.user.id}
        ownerId={
          conversationDetails?.created_by_id === -2
            ? 3
            : conversationDetails?.created_by_id
        }
      />

      <div className="h-94 flex flex-col justify-between">
        <Messages
          accessToken={accessToken}
          chatId={chatId}
          isSending={isMessageUpdating || isMessageSending}
          hasClickedLoadMore={hasClickedLoadMore}
          setHasClickedLoadMore={setHasClickedLoadMore}
        />

        {isAnnouncement && currentUser?.user_id !== 3 ? null : (
          <div className="flex items-center justify-between gap-md mg-0-auto mg-b-md pd-l-sm pd-r-sm w-96 h-80px">
            <ConversationMessageInput
              setMessage={setMessage}
              message={message}
              sendMessage={sendMessage}
            />
            <Button
              variant="success"
              disabled={isMessageSending || isMessageUpdating}
              onClick={sendMessage}
              size="md"
            >
              {isMessageSending || isMessageUpdating ? (
                <LoadingIcon size="lg" className="text-dark" />
              ) : (
                <Icon
                  icon={
                    isNil(messageToEdit)
                      ? ["far", "paper-plane"]
                      : ["fad", "check"]
                  }
                  size="lg"
                  className="text-dark pointer borderR-02 h-full "
                />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
